import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppShell,
  Callout,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  type CurrencyOption,
  type EntryCurrency,
  type SearchHit,
} from '@biovet/ui';
import { MODULES, visibleModules, withBadges } from './nav.js';
import { HOME, moduleOf, useRoute, withHrefs } from './lib/router.js';
import { ChartOfAccountsScreen } from './pages/ChartOfAccounts.js';
import { GeneralLedgerScreen } from './pages/GeneralLedger.js';
import { TrialBalanceScreen } from './pages/TrialBalance.js';
import { BalanceSheetScreen, ProfitAndLossScreen } from './pages/Statements.js';
import { VoucherEntryScreen } from './pages/VoucherEntry.js';
import { ChangePasswordScreen } from './pages/ChangePassword.js';
import { LoginScreen } from './pages/Login.js';
import { VoucherDetailModal } from './pages/VoucherDetailModal.js';
import { DashboardScreen } from './pages/Dashboard.js';
import { RecordsScreen } from './pages/Records.js';
import { DealEntryScreen } from './pages/DealEntry.js';
import { DealPartiesScreen } from './pages/DealParties.js';
import {
  AvailabilityScreen,
  CommissionInvoiceScreen,
  DealRegisterScreen,
  PendingSettlementScreen,
} from './pages/Brokerage.js';
import { CommissionRunScreen, SaleReturnScreen, SalesInvoiceScreen } from './pages/Sales.js';
import {
  ImportCostingScreen,
  ImportShipmentsScreen,
  PurchaseEntryScreen,
  PurchaseReturnScreen,
} from './pages/Purchase.js';
import {
  BatchExpiryScreen,
  PhysicalCountScreen,
  ProductsScreen,
  StockLedgerScreen,
  StockTransferScreen,
  WarehouseStockScreen,
} from './pages/Inventory.js';
import { ArAgeingScreen, ApAgeingScreen, CashPositionScreen } from './pages/CashAgeing.js';
import {
  ArApAgeingScreen,
  AuditTrailScreen,
  GrossMarginScreen,
  PurchaseRegisterScreen,
  SalesByZoneScreen,
  SalesRegisterScreen,
} from './pages/ManagementReports.js';
import { RecentActivityScreen } from './pages/RecentActivity.js';
import { BankReconciliationScreen } from './pages/BankReconciliation.js';
import { UsersZonesScreen } from './pages/UsersZones.js';
import { CategoriesScreen } from './pages/Categories.js';
import { PartiesScreen } from './pages/Parties.js';
import { SetupScreen, type SetupKey } from './pages/Setup.js';
import { ReportsScreen } from './pages/Reports.js';
import { RECORD_SETS } from './lib/design-data.js';
import { isVoucherTypeCode, type VoucherTypeCode } from './lib/voucher-config.js';
import {
  companyLocation,
  companyToday,
  defaultAccountOfKind,
  setAccounts,
  setCompany,
  setCurrencies,
  setFiscalCalendar,
  setUoms,
  type Account,
  type PeriodStatus,
} from './lib/reference-data.js';
import { forgetSignIn, hasSignInHint, rememberSignIn } from './lib/session.js';
import {
  apiGet,
  apiSend,
  useApiBusy,
  useCan,
  useResource,
  type ApiAccount,
  type ApiContext,
  type NavCounts,
  type Session,
} from './lib/api.js';
import type { Role } from './lib/voucher-model.js';

/**
 * The application.
 *
 * Sign-in is the outer gate, and the book lives in its own component rather than
 * behind an early return inside this one: hooks cannot be conditional, and a
 * `useResource` that fires while nobody is signed in is a request the API should
 * never have been asked for.
 */
export function App() {
  /**
   * Whether this browser believes it has a session — a **hint**, not a
   * credential.
   *
   * The session itself is an `HttpOnly` cookie this code cannot read, which is
   * the point: what used to live here was the signed-in email, and the API
   * trusted it. Editing that value in devtools promoted you. Editing this one
   * gets you a sign-in screen or a 401.
   *
   * It exists at all so a refresh does not flash the login card at somebody who
   * is already signed in — read synchronously as the initialiser rather than in
   * an effect for exactly that reason. The API is still the only thing that
   * decides, and `onSessionRejected` clears the hint the moment it disagrees.
   */
  const [signedIn, setSignedIn] = useState<boolean>(hasSignInHint);

  /**
   * Bumped when "view as" changes which user the session acts as.
   *
   * The switch happens on the server now, so there is no email to key the
   * subtree on. This does the same job: a new key rebuilds the shell rather than
   * carrying one user's branch, currency and half-typed voucher into another's
   * view.
   */
  const [epoch, setEpoch] = useState(0);

  const signIn = () => {
    rememberSignIn();
    setSignedIn(true);
  };

  const signOut = () => {
    forgetSignIn();
    setSignedIn(false);
    setEpoch((n) => n + 1);
  };

  if (!signedIn) return <LoginScreen onSignIn={signIn} />;
  return (
    <Book
      key={epoch}
      onViewChanged={() => setEpoch((n) => n + 1)}
      onSignOut={signOut}
      onSessionRejected={signOut}
    />
  );
}

/**
 * The signed-in shell.
 *
 * The nav key in the address bar selects the screen — see `lib/router.ts` — and the data is real where the
 * data exists. The session, the chart of accounts and the company context come
 * from `apps/api`, and what the signed-in user may see follows from their
 * permissions rather than from a constant in this file. The registers and
 * reports built from the design run on `design-data.ts` until their endpoints
 * exist.
 *
 * Currency, rate and branch are session settings on the top bar rather than
 * fields on a voucher.
 */
function Book({
  onViewChanged,
  onSignOut,
  onSessionRejected,
}: {
  /** A super administrator switched view; rebuild the shell around the new one. */
  onViewChanged: () => void;
  onSignOut: () => void;
  /** The API refused the session cookie — drop the hint and start over. */
  onSessionRejected: () => void;
}) {
  /**
   * Which screen is showing — and it is the address bar that says so.
   *
   * This was two `useState` keys, which is why a rail item could not be opened
   * in a second tab and why the back button left the application entirely.
   * `useRoute` keeps the pair and the URL in step; everything below navigates
   * through it rather than setting the two independently.
   */
  const { route, navigate: go } = useRoute();
  const { module, item } = route;
  /**
   * The account the General Ledger opens on: the till.
   *
   * It was `1131 Accounts Receivable — Customers`, which since `0030` is a
   * control account with one child per customer — so the screen opened on a head
   * that holds no lines of its own and showed nothing on a book with customers.
   * Cash in Hand is a leaf, it is where the most-read ledger in any small book
   * lives, and it has something on it as soon as anybody posts a receipt.
   *
   * It was then the literal `'1111'`, which 0037 renumbered to `1-1-1-1`. The
   * account is read out of the chart now, by control kind — the same registry the
   * cash voucher's contra reads — so this is right in a book whose chart was
   * built rather than seeded, and empty rather than wrong before the chart loads.
   */
  const [ledgerAccount, setLedgerAccount] = useState('');
  /** Which deal the brokerage module is looking at. Null opens on the newest. */
  const [dealNo, setDealNo] = useState<string | null>(null);
  /** Which consignment the costing sheet is looking at. Null opens on the newest. */
  const [shipmentNo, setShipmentNo] = useState<string | null>(null);
  /**
   * The entry currency, and one rate per currency rather than one USD rate.
   *
   * `usdRate` was a single string, which is why a currency added under Setup →
   * Currencies could be posted by the database and not chosen in the top bar:
   * there was nowhere to put its rate. `rates` is keyed by code and seeded from
   * `/api/context`, so a new currency arrives with its own live rate and an
   * edit to one currency's rate cannot silently move another's.
   *
   * The currency itself opens on **base**, whatever base is, rather than on the
   * literal 'PKR' — a book set up in another currency should not open every
   * voucher in a foreign one.
   */
  const [currency, setCurrency] = useState<EntryCurrency>('');
  const [rates, setRates] = useState<Record<string, string>>({});
  const [branchCode, setBranchCode] = useState('HO');
  /**
   * A voucher opened from search, over whatever screen is showing.
   *
   * Held here rather than on a screen because search reaches it from anywhere:
   * finding `JV-26-000418` from the inventory module should show the voucher,
   * not navigate to the ledger first and open it there.
   */
  const [openVoucher, setOpenVoucher] = useState<{ type: string; no: string } | null>(null);

  // No parameter: the server reads the session cookie and answers who that is.
  const sessionRes = useResource<Session>('/session');
  const contextRes = useResource<ApiContext>('/context');
  const accountsRes = useResource<{ accounts: ApiAccount[] }>('/accounts');
  const session = sessionRes.data;
  const can = useCan(session);

  /**
   * "View as" is the super administrator's alone, and the switch is a write.
   *
   * Being able to look at the books through somebody else's permissions is
   * itself a permission, and the strongest one in the application — it is the
   * ability to bypass every other gate.
   *
   * It used to be this component setting a different email, which meant it was
   * not a permission at all: it was a text field, and anybody who could edit
   * `localStorage` held it. Now it is `POST /api/session/view-as`, which reads
   * `users.is_superadmin` out of the database before it writes
   * `user_sessions.acting_user_id`. This component decides whether to render the
   * switcher; the server decides whether the switch happens, and the two
   * disagreeing costs a 403 rather than an escalation.
   *
   * The roster is still fetched only when it can be used, so an accountant's
   * browser is never handed a list of accounts it has no business knowing about.
   */
  const impersonator = Boolean(session?.isSuperAdmin) || Boolean(session?.viewingAs);
  const usersRes = useResource<{ users: DirectoryUser[] }>(impersonator ? '/users' : null);
  const viewAs = useMemo(
    () =>
      impersonator
        ? (usersRes.data?.users ?? []).map((u) => ({
            value: u.email,
            label: u.roleName,
            hint: `${u.fullName} · ${u.scopeName}`,
          }))
        : undefined,
    [impersonator, usersRes.data],
  );

  const switchView = useCallback(
    (target: string) => {
      void apiSend('POST', '/session/view-as', { email: target }).then(
        onViewChanged,
        // A refused switch leaves the session exactly as it was; re-reading it
        // is what puts the switcher back on the user the server still says we
        // are, rather than on the one the click optimistically selected.
        () => sessionRes.reload(),
      );
    },
    [onViewChanged, sessionRes],
  );

  /**
   * The chart of accounts feeds both the pickers and the validation rules, so it
   * lands in the module-level registry as well as in state — `findAccount()` is
   * called from pure functions that have no access to a React tree.
   */
  // The API already filters to active, undeleted accounts.
  const accounts = useMemo<Account[]>(() => {
    const list = (accountsRes.data?.accounts ?? []).map(toAccount);
    // Filled here rather than in an effect, and that is load-bearing.
    //
    // Effects run *after* children render, so a screen that memoises over the
    // registry — `accountOptionsFor()` on the voucher form does exactly that —
    // computed once against an empty one and then never recomputed, because
    // filling a module-level cache changes no prop and schedules no render. The
    // symptom was an account picker that said "No account matches" for every
    // account in the chart, but only when a voucher screen was the first screen
    // rendered: reaching it from the dashboard was fine, and a deep link or a
    // refresh on `/finance/cp` was not.
    //
    // A write during render, which React tolerates because it is idempotent:
    // the same data produces the same cache, and StrictMode's double invocation
    // is therefore invisible.
    setAccounts(list);
    return list;
  }, [accountsRes.data]);

  /**
   * The till, for the General Ledger's opening account.
   *
   * Derived from the chart rather than written as `'1111'`, and memoised over
   * `accounts` rather than read once — the registry is filled during this render,
   * so anything computed before it lands is computed against an empty one and
   * never recomputed. Empty until the chart arrives, which the screen already
   * handles as "no account chosen".
   */
  const defaultTill = useMemo(() => defaultAccountOfKind('CASH'), [accounts]);

  /**
   * Sign out on the server, then locally.
   *
   * The order matters and the `finally` matters more: clearing the local hint
   * only after the request succeeded would strand somebody on a screen they
   * cannot use when the network is down, and the cookie is revoked server-side
   * either way the moment it is next presented and found expired.
   */
  const handleSignOut = useCallback(() => {
    void apiSend('POST', '/logout').finally(onSignOut);
  }, [onSignOut]);

  /**
   * The register, and the rates in it, are the company's.
   *
   * Seeded once per context load and then left alone: an administrator editing
   * a rate in the top bar is making a session-wide correction, and refetching
   * would throw it away the next time anything re-rendered. A currency whose
   * `rate` is null is deliberately left out of the map rather than defaulted to
   * 1 — `rateFor` below turns that absence into an empty field the accountant
   * has to fill, not a par rate that quietly posts.
   */
  const currencies = useMemo(() => contextRes.data?.currencies ?? [], [contextRes.data]);
  const baseCurrency = contextRes.data?.company.baseCurrency ?? '';

  // Into the module-level registry as well as into state, the same way the
  // chart of accounts goes — and during render for the same reason: the
  // currency pickers inside Deal Entry and the brokerage panels are four
  // components deep, read the registry directly, and several of them memoise.
  setCurrencies(currencies);
  setUoms(contextRes.data?.uoms ?? []);

  /**
   * The company and the fiscal calendar, into their registries.
   *
   * Both are reference data the way the chart of accounts is, and both are read
   * from outside the React tree: `validate()` judges a voucher date against the
   * open year, and the printed voucher builds its own letterhead. The calendar
   * was a pair of constants naming FY 2026 and twelve months — every book was
   * that book, and a period the database had closed still looked open here.
   */
  {
    const ctx = contextRes.data;
    setCompany(ctx ? ctx.company : null);
    setFiscalCalendar(
      ctx?.fiscalYear ?? null,
      (ctx?.periods ?? []).map((p) => ({
        periodNo: p.periodNo,
        name: p.name,
        startDate: p.startDate,
        endDate: p.endDate,
        status: p.status as PeriodStatus,
        isAdjustment: p.isAdjustment,
      })),
    );
  }

  useEffect(() => {
    if (currencies.length === 0) return;
    setRates((prior) => {
      const next = { ...prior };
      for (const c of currencies) if (c.rate && next[c.code] === undefined) next[c.code] = c.rate;
      return next;
    });
    // Opens on base. Also repairs a selection that no longer exists — a
    // currency can be deactivated in Setup while somebody has it chosen.
    setCurrency((prior) =>
      prior && currencies.some((c) => c.code === prior) ? prior : (baseCurrency ?? ''),
    );
  }, [currencies, baseCurrency]);

  /** The rate for a currency: base is 1 by definition, everything else is looked up. */
  const rateFor = useCallback(
    (code: string): string => (code === baseCurrency ? '1' : (rates[code] ?? '')),
    [baseCurrency, rates],
  );

  /**
   * Make the rate typed in the top bar the book's rate.
   *
   * Editing the field alone was a **session** setting — this tab converted at
   * the new number, no other tab did, and a reload lost it. That is the failure
   * the top-bar control existed to prevent: one rate, set once, read by every
   * screen. Applying it writes today's `SPOT` row through the same endpoint
   * Setup → Tax & FX writes, so there is one register and one code path rather
   * than a second, quieter way for a rate to enter the book.
   *
   * `contextRes.reload()` afterwards is what closes the loop: the register is
   * where the menu's "1 USD = …" hints and every screen's opening rate come
   * from, and leaving it stale would leave the panel disagreeing with the row
   * it had just written. The rejection is deliberately not swallowed — the
   * server refusing for want of `finance.fx` is a real answer, and the menu
   * shows its sentence.
   */
  const applyRate = useCallback(
    async (code: EntryCurrency, rate: string) => {
      await apiSend('POST', '/setup/fx-rates', {
        // The company's today, not the browser's. A book kept in Karachi and
        // opened from London would otherwise date the evening's rate tomorrow.
        rateDate: companyToday(),
        fromCurrency: code,
        toCurrency: baseCurrency,
        rateType: 'SPOT',
        rate,
        source: 'Top bar',
      });
      contextRes.reload();
    },
    [baseCurrency, contextRes],
  );

  useEffect(() => {
    const preferred = session?.branches.find((b) => b.isDefault) ?? session?.branches[0];
    if (preferred) setBranchCode(preferred.code);
  }, [session]);

  /**
   * A session the server will not honour is not a session.
   *
   * **401** is the cookie being missing, expired, revoked or signed out from
   * another browser — the ordinary end of a session, and the server has already
   * cleared the cookie on its way past. **404** is the rarer case of a live
   * session whose user has since been deactivated or deleted.
   *
   * Only those two clear the local hint. A missing status means the request
   * never arrived at all, and signing somebody out because `pnpm api` was not
   * running yet would be its own bug — that is what the "API is not answering"
   * screen is for.
   */
  useEffect(() => {
    if (sessionRes.status === 401 || sessionRes.status === 404) onSessionRejected();
  }, [sessionRes.status, onSessionRejected]);

  /**
   * The rail, filtered to what this session's role actually holds.
   *
   * This used to be one special case — `m.key === 'setup' ? can('admin.company')
   * : true` — so Setup was gated and the other fifty items were not. A role
   * without `finance.trial_balance` was still shown Trial Balance in the rail,
   * and clicking it drew the whole trial balance, because the read behind it was
   * authenticated and not authorised.
   *
   * Every item now names its `permission_modules` code in `nav.ts` and
   * `visibleModules` drops the ones that are not held, groups included. Setup
   * needs no mention here any more: its seven items each name a Setup module,
   * `seed/0004_access.sql` revokes that group from every role but SUPERADMIN, so
   * the group empties itself and disappears by the general rule.
   *
   * **This is the courtesy, not the gate.** `READ_MODULE` refuses the same reads
   * on the server. Nothing here decides anything a `fetch` could not undo.
   */
  const visibleNav = useMemo(() => visibleModules(MODULES, can), [can]);

  /**
   * The nav badges, counted.
   *
   * Refetched whenever the nav item changes rather than on a timer: posting a
   * bank receipt or acknowledging a settlement is what moves these numbers, and
   * both end with the user navigating somewhere. A polling interval would be a
   * request every few seconds for two integers that change a handful of times a
   * day.
   */
  const navCountsRes = useResource<NavCounts>('/nav-counts');
  const reloadNavCounts = navCountsRes.reload;
  useEffect(() => {
    reloadNavCounts();
  }, [item, reloadNavCounts]);

  // `withHrefs` is what turns the rail into links: the shell renders an anchor
  // wherever it finds an `href`, so every screen can be opened in a new tab.
  const modules = useMemo(
    () => withHrefs(withBadges(visibleNav, navCountsRes.data)),
    [visibleNav, navCountsRes.data],
  );

  /**
   * A hidden module must not stay selected — switching user should not leave an
   * accountant looking at a Setup screen they can no longer reach, and now that
   * screens have addresses it must also not leave one at `/setup/company` after
   * typing it in.
   *
   * `replace`, because that address was never a place this session could be: it
   * should not be somewhere the back button returns to.
   *
   * **It waits for the session, and that guard is the whole of the deep-link
   * bug.** `can` answers `false` for everything until `GET /api/session` lands,
   * so on a cold load `visibleModules` kept exactly one module — Dashboard,
   * whose Overview item names no permission — and this effect read the other
   * eleven as "not held" and replaced the address with `/dashboard/overview`.
   * Not a redirect anybody could see happening: it fired before the first paint,
   * so a refresh, a bookmark and an "open in new tab" all looked like the
   * application simply ignoring the address. And `replace` made it unrecoverable
   * — the address it destroyed was not even in the history to go back to.
   *
   * The session arriving does not undo it, because by then the route *is*
   * dashboard and there is nothing left to say where the user meant to be. So
   * the fix has to be not doing it in the first place: no permission is known
   * before the session, and "unknown" is not "refused".
   */
  useEffect(() => {
    if (!session) return;
    if (!modules.some((m) => m.key === module)) {
      const first = modules[0];
      if (first) {
        go({ module: first.key, item: first.items[0]?.key ?? HOME.item }, { replace: true });
      }
    }
  }, [session, modules, module, go]);

  const handleModuleChange = (key: string) => {
    const first = modules.find((m) => m.key === key)?.items[0];
    if (first) go({ module: key, item: first.key });
  };

  const navigate = (nextModule: string, nextItem: string) => {
    go({ module: nextModule, item: nextItem });
  };

  const openLedger = (code: string) => {
    setLedgerAccount(code);
    navigate('finance', 'gl');
  };

  /** A row in the deal register is a link into the entry screen for that deal. */
  const openDeal = (no: string) => {
    setDealNo(no);
    navigate('brokerage', 'deal');
  };

  /** A consignment on the shipment register is a link into its costing sheet. */
  const openCosting = (no: string) => {
    setShipmentNo(no);
    navigate('sales', 'costing');
  };

  /** Navigate by nav key alone, finding the module that owns it. */
  const openItem = (key: string) => {
    const owner = moduleOf(key);
    if (owner) navigate(owner, key);
  };

  /**
   * Anything in flight anywhere, which the shell turns into the bar above the
   * content.
   *
   * Read at the top rather than per screen on purpose: a nav click typically
   * starts three or four reads across the page, and one indicator for all of
   * them is both truthful and quiet. The grey placeholders still exist for a
   * screen with nothing to show; this is what answers the click itself.
   */
  const busy = useApiBusy();

  const isAdmin = Boolean(session?.isSuperAdmin);
  const canSwitchBranch = isAdmin || can('admin.zone', 'VIEW');

  /**
   * What the top bar's search box finds, and what happens when you pick it.
   *
   * The shell owns the palette — the keyboard, the debounce, the list — and
   * knows nothing about accounts or vouchers. This is the other half: one call
   * to `/api/search`, and a `hit.id` shaped `kind:key` so `onSelect` can route
   * without a second lookup. Nav items are matched inside the shell from
   * `modules`, so they are absent here on purpose.
   */
  const searchHits = useCallback(async (query: string): Promise<SearchHit[]> => {
    const { hits } = await apiGet<{ hits: ApiSearchHit[] }>('/search', { q: query });
    return hits.map((hit) => ({
      id: `${hit.kind}:${hit.voucherType ?? ''}:${hit.key}`,
      label: hit.label,
      hint: hit.hint ?? undefined,
      group: GROUP_LABEL[hit.kind],
    }));
  }, []);

  /** `kind:qualifier:key` — the qualifier is the voucher type, empty otherwise. */
  const openSearchHit = (hit: SearchHit) => {
    const [kind, qualifier, ...rest] = hit.id.split(':');
    const key = rest.join(':');

    if (kind === 'nav') openItem(key);
    else if (kind === 'account') openLedger(key);
    else if (kind === 'voucher' && qualifier) setOpenVoucher({ type: qualifier, no: key });
  };

  /**
   * A one-time password has to be replaced before anything else is reachable.
   *
   * Rendered *instead of* the shell rather than over it: the server refuses
   * every route but a small allowlist while the flag is set, so there is no
   * application behind this card to show. Drawing one and then failing every
   * click would be a lie about where you are.
   *
   * `sessionRes.reload()` afterwards rather than a local flag — the server is
   * what decides the state is cleared, and re-reading is what proves it.
   */
  if (session?.mustChangePassword) {
    return (
      <ChangePasswordScreen
        fullName={session.fullName}
        onChanged={() => {
          sessionRes.reload();
          accountsRes.reload();
          contextRes.reload();
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <AppShell
      // Every part of this is Setup → Company Profile now. It used to be the
      // name from the API and two literals, so a book that had set a logo and a
      // tagline still showed a jade 'b' and the word "Accounting".
      brand={{
        name: contextRes.data?.company.name ?? 'biovet',
        tagline: contextRes.data?.company.tagline ?? undefined,
        mark: contextRes.data?.company.brandMark ?? undefined,
        logoUrl: contextRes.data?.company.logoUrl ?? undefined,
        // The clock sits under the name, in the timezone the books are kept in
        // — which is the only timezone in which "is this in today's period?"
        // has an answer.
        timezone: contextRes.data?.company.timezone ?? undefined,
      }}
      // What a printed page from this application says it came from. One
      // definition for every screen, and every field of it is Setup's.
      letterhead={
        contextRes.data
          ? {
              name: contextRes.data.company.name,
              legalName: contextRes.data.company.legalName,
              logoUrl: contextRes.data.company.logoUrl ?? undefined,
              location: companyLocation(),
              phone: contextRes.data.company.phone ?? undefined,
              email: contextRes.data.company.email ?? undefined,
              ntn: contextRes.data.company.ntn ?? undefined,
              strn: contextRes.data.company.strn ?? undefined,
              printedAt: companyToday(),
            }
          : undefined
      }
      modules={modules}
      activeModule={module}
      onModuleChange={handleModuleChange}
      activeItem={item}
      onItemChange={(key) => navigate(module, key)}
      busy={busy}
      user={{
        name: session?.fullName ?? '—',
        initials: session?.initials ?? '··',
        jobTitle: session?.jobTitle ?? undefined,
      }}
      fx={{
        currency,
        currencies,
        rate: rateFor(currency),
        onCurrencyChange: setCurrency,
        // The edit lands on the currency that is selected. One shared rate
        // field would have written a EUR correction into the USD rate.
        onRateChange: (rate) => setRates((prior) => ({ ...prior, [currency]: rate })),
        canEditRate: isAdmin,
        onApplyRate: applyRate,
      }}
      branch={{
        options: session?.branches.map((b) => ({ code: b.code, name: b.name })) ?? [],
        active: branchCode,
        onChange: setBranchCode,
        canSwitch: canSwitchBranch,
      }}
      search={{ find: searchHits, onSelect: openSearchHit }}
      roles={viewAs}
      activeRole={session?.email}
      onRoleChange={impersonator ? switchView : undefined}
      onSignOut={handleSignOut}
    >
      {sessionRes.error ? (
        <ApiDown message={sessionRes.error} />
      ) : (
        <Screen
          item={item}
          module={module}
          session={session}
          accounts={accounts}
          currency={currency}
          fxRate={rateFor(currency)}
          currencies={currencies}
          baseCurrency={baseCurrency}
          usdRate={rateFor('USD')}
          branchCode={branchCode}
          ledgerAccount={ledgerAccount || defaultTill}
          onLedgerAccountChange={setLedgerAccount}
          dealNo={dealNo}
          onDealChange={setDealNo}
          onOpenDeal={openDeal}
          shipmentNo={shipmentNo}
          onShipmentChange={setShipmentNo}
          onOpenCosting={openCosting}
          onOpenLedger={openLedger}
          onOpenItem={openItem}
          onNavigate={navigate}
          onChartChanged={accountsRes.reload}
        />
      )}

      <VoucherDetailModal
        voucherType={openVoucher?.type ?? null}
        voucherNo={openVoucher?.no ?? null}
        onClose={() => setOpenVoucher(null)}
        onOpenLedger={(code) => {
          setOpenVoucher(null);
          openLedger(code);
        }}
      />
    </AppShell>
  );
}

/** The heading a hit sits under in the palette. */
const GROUP_LABEL: Record<ApiSearchHit['kind'], string> = {
  account: 'Accounts',
  voucher: 'Vouchers',
};

/** One row of `GET /api/search`. */
interface ApiSearchHit {
  kind: 'account' | 'voucher';
  key: string;
  label: string;
  hint: string | null;
  voucherType: string | null;
}

/**
 * The nav key → screen table.
 *
 * Named screens are matched first, then the five voucher codes, then the
 * design's registers and reports, then the placeholder. That order is deliberate:
 * `isVoucherTypeCode` matches on shape rather than on an enumerated list, so a
 * future nav key that happens to read like a voucher code must not be swallowed
 * by it.
 */
function Screen({
  item,
  module,
  session,
  accounts,
  currency,
  fxRate,
  currencies,
  baseCurrency,
  usdRate,
  branchCode,
  ledgerAccount,
  onLedgerAccountChange,
  dealNo,
  onDealChange,
  onOpenDeal,
  shipmentNo,
  onShipmentChange,
  onOpenCosting,
  onOpenLedger,
  onOpenItem,
  onNavigate,
  onChartChanged,
}: {
  item: string;
  module: string;
  session: Session | undefined;
  accounts: Account[];
  currency: EntryCurrency;
  /** The selected currency's rate into base. '1' on base, '' when none is set. */
  fxRate: string;
  currencies: CurrencyOption[];
  baseCurrency: string;
  /** The live USD rate specifically, for import costing. */
  usdRate: string;
  branchCode: string;
  ledgerAccount: string;
  onLedgerAccountChange: (code: string) => void;
  dealNo: string | null;
  onDealChange: (dealNo: string | null) => void;
  onOpenDeal: (dealNo: string) => void;
  shipmentNo: string | null;
  onShipmentChange: (shipmentNo: string | null) => void;
  onOpenCosting: (shipmentNo: string) => void;
  onOpenLedger: (code: string) => void;
  onOpenItem: (key: string) => void;
  onNavigate: (module: string, item: string) => void;
  /** Re-fetch the chart after it is edited, so every picker sees the change. */
  onChartChanged: () => void;
}) {
  switch (item) {
    case 'overview':
      return (
        <DashboardScreen
          userName={firstNameOf(session)}
          role={roleOf(session)}
          onOpenItem={onNavigate}
        />
      );
    case 'coa':
      return <ChartOfAccountsScreen onOpenLedger={onOpenLedger} onChartChanged={onChartChanged} />;
    case 'gl':
      return (
        <GeneralLedgerScreen accountCode={ledgerAccount} onAccountChange={onLedgerAccountChange} />
      );
    case 'tb':
      return <TrialBalanceScreen onOpenLedger={onOpenLedger} />;
    case 'pl':
      return <ProfitAndLossScreen />;
    case 'bs':
      return <BalanceSheetScreen />;
    case 'deal':
      return (
        <DealEntryScreen branchCode={branchCode} dealNo={dealNo} onDealChange={onDealChange} />
      );
    case 'deal-register':
      return <DealRegisterScreen onOpenDeal={onOpenDeal} />;
    case 'deal-parties':
      return <DealPartiesScreen />;
    case 'availability':
      return <AvailabilityScreen />;
    case 'commission-invoice':
      return <CommissionInvoiceScreen />;
    case 'settlement':
      return <PendingSettlementScreen />;
    case 'shipments':
      // The consignment form books Accounts Payable — Foreign at this rate, so
      // it opens on the session's rather than on a constant compiled in months
      // ago. `usdRate` comes from `fx_rates` through `/api/context`.
      return <ImportShipmentsScreen onOpenCosting={onOpenCosting} sessionFxRate={usdRate} />;
    case 'costing':
      return <ImportCostingScreen shipmentNo={shipmentNo} onShipmentChange={onShipmentChange} />;
    case 'purchase':
      return <PurchaseEntryScreen />;
    case 'preturn':
      return <PurchaseReturnScreen />;
    case 'invoice':
      return <SalesInvoiceScreen />;
    case 'sreturn':
      return <SaleReturnScreen />;
    case 'commission-run':
      return <CommissionRunScreen />;
    case 'parties':
      return <PartiesScreen />;
    case 'products':
      return <ProductsScreen />;
    case 'categories':
      return <CategoriesScreen />;
    case 'stock':
      return <WarehouseStockScreen />;
    case 'stock-ledger':
      return <StockLedgerScreen />;
    case 'transfer':
      return <StockTransferScreen />;
    case 'count':
      return <PhysicalCountScreen />;
    case 'batch':
      return <BatchExpiryScreen />;
    case 'hierarchy':
    case 'roles':
    case 'zones':
      return <UsersZonesScreen focus={item} />;
    case 'company':
    case 'fy':
    case 'numbering':
    case 'fx':
    case 'branches':
    case 'currencies':
    case 'uoms':
    case 'links':
      return <SetupScreen section={item as SetupKey} />;
    case 'cash-position':
      return <CashPositionScreen />;
    case 'recon':
      return <BankReconciliationScreen />;
    case 'receivables':
      return <ArAgeingScreen />;
    case 'payables':
      return <ApAgeingScreen />;
    // The report-library item says "AR / AP Ageing" and used to render only the
    // receivables half.
    case 'rpt-ageing':
      return <ArApAgeingScreen />;
    case 'rpt-saleszone':
      return <SalesByZoneScreen />;
    case 'rpt-margin':
      return <GrossMarginScreen />;
    case 'rpt-sales-register':
      return <SalesRegisterScreen />;
    case 'rpt-purchase-register':
      return <PurchaseRegisterScreen />;
    case 'rpt-audit':
      return <AuditTrailScreen />;
    // Was missing entirely, so the nav item fell through to `Pending`.
    case 'activity':
      return <RecentActivityScreen />;
    case 'library':
      return <ReportsScreen onOpen={onOpenItem} />;
    default:
      break;
  }

  if (isVoucherTypeCode(item)) {
    // Keyed so switching voucher type resets the draft rather than carrying a
    // half-entered Cash Payment into a Bank Receipt.
    return (
      <VoucherEntryScreen
        key={item}
        type={item.toUpperCase() as VoucherTypeCode}
        role={roleOf(session)}
        currency={currency}
        // Resolved once in App from the currency register, so a currency added
        // in Setup carries its own rate into the form and the GL effect card.
        // This used to read `currency === 'PKR' ? '1' : usdRate`, which gave
        // every non-USD currency the USD rate.
        fxRate={fxRate}
        baseCurrency={baseCurrency}
        branchCode={branchCode}
        accounts={accounts}
      />
    );
  }

  // Keyed so the filter chip resets when the nav moves to a different register.
  if (RECORD_SETS[item]) return <RecordsScreen key={item} recordKey={item} />;

  return <Pending module={module} item={item} />;
}

/** One row of `GET /api/users` — only ever fetched by a super administrator. */
interface DirectoryUser {
  email: string;
  fullName: string;
  roleName: string;
  scopeName: string;
}

/** `Ayesha Raza` → `Ayesha`. The dashboard greets; it does not address. */
function firstNameOf(session: Session | undefined): string {
  return session?.fullName?.split(' ')[0] ?? 'there';
}

/** The approval gate in the voucher form still speaks in the three old names. */
function roleOf(session: Session | undefined): Role {
  if (!session) return 'accountant';
  if (session.isSuperAdmin || session.roleCode === 'OWNER' || session.roleCode === 'ADMIN') {
    return 'owner';
  }
  return session.roleCode === 'MANAGER' ? 'manager' : 'accountant';
}

function toAccount(account: ApiAccount): Account {
  return {
    code: account.code,
    name: account.name,
    type: account.type,
    normalBalance: account.normalBalance,
    control: (account.controlKind as Account['control']) ?? null,
    allowDirectPosting: account.allowDirectPosting,
    fcyCurrency: account.fcyCurrency,
    requiresParty: account.requiresParty || isSubLedger(account.controlKind),
    isLeaf: account.isLeaf,
    level: account.level,
    parentCode: account.parentCode,
    childCount: account.childCount,
    balanceBase: account.balanceBase,
    isBank: account.isBank,
  };
}

/**
 * `accounts.require_party` is off in the seed, but a receivable or payable line
 * with no party cannot be reconciled against its sub-ledger. The screen is the
 * stricter of the two on purpose.
 */
function isSubLedger(controlKind: string | null): boolean {
  return (
    controlKind === 'RECEIVABLE' ||
    controlKind === 'PAYABLE' ||
    controlKind === 'COMMISSION_RECEIVABLE' ||
    controlKind === 'COMMISSION_PAYABLE'
  );
}

function ApiDown({ message }: { message: string }) {
  return (
    <>
      <PageHeader
        breadcrumb="biovet Accounting"
        title="The API is not answering"
        description="The application cannot reach the server, so there are no figures to show."
      />
      <Card flush>
        <CardHeader title="Start the stack" subtitle="Three commands, one at a time" />
        <CardBody className="flex flex-col gap-2.5">
          <Callout tone="danger" title="What the browser saw">
            {message}
          </Callout>
          <Callout tone="info" title="1 · Database">
            <code className="bb-mono">pnpm infra:up</code> then{' '}
            <code className="bb-mono">pnpm db:migrate</code> and{' '}
            <code className="bb-mono">pnpm db:seed</code>. Docker Desktop must be running.
          </Callout>
          <Callout tone="info" title="2 · API">
            <code className="bb-mono">pnpm api</code> — serves on 3001, which Vite proxies.
          </Callout>
          <Callout tone="info" title="3 · This app">
            <code className="bb-mono">pnpm dev</code>
          </Callout>
        </CardBody>
      </Card>
    </>
  );
}

function Pending({ module, item }: { module: string; item: string }) {
  const mod = MODULES.find((m) => m.key === module);
  const nav = mod?.items.find((i) => i.key === item);

  return (
    <>
      <PageHeader
        breadcrumb={`${mod?.label ?? ''} / ${nav?.label ?? ''}`}
        title={nav?.label ?? 'Screen'}
        description="This screen is planned and has not been built yet."
      />

      <Card flush>
        <CardHeader
          title="What already exists behind this screen"
          subtitle="Schema, invariants and reporting objects are live"
        />
        <CardBody className="flex flex-col gap-2.5">
          <Callout tone="success" title="Database">
            Tables, constraints and PL/pgSQL invariants are written and tested. Run{' '}
            <code className="bb-mono">pnpm db:test</code> to see the four ledger invariants enforced
            against real postings.
          </Callout>
          <Callout tone="warn" title="Not yet built">
            This screen&rsquo;s endpoint and form, the NestJS API and the CASL authorisation layer.
            See <code className="bb-mono">docs/roadmap.md</code>.
          </Callout>
        </CardBody>
      </Card>
    </>
  );
}
